import { useCallback, useEffect, useState } from 'react';

// Scroll state and paging for a horizontally scrolling row. Put `ref` on the
// scrolling element. `canScrollLeft` / `canScrollRight` say whether there's
// more to either side (both false: everything fits), and `scrollPage` moves
// by as many whole items as fit, landing on an item's edge.
export const useHorizontalScroll = <T extends HTMLElement>() => {
  // Held in state (a callback ref) so the listeners still attach when the
  // element mounts after the first render, e.g. behind a loading state.
  const [element, setElement] = useState<T | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const update = useCallback(() => {
    if (!element) return;
    // 1px of slack: scroll positions can be fractional when zoomed.
    setCanScrollLeft(element.scrollLeft > 1);
    setCanScrollRight(
      element.scrollLeft + element.clientWidth < element.scrollWidth - 1,
    );
  }, [element]);

  useEffect(() => {
    if (!element) return undefined;
    update();
    element.addEventListener('scroll', update, { passive: true });
    // A resize changes how much fits; adding, removing or filtering items
    // changes the content without resizing the row itself.
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(element);
    const mutationObserver = new MutationObserver(update);
    mutationObserver.observe(element, { childList: true });
    return () => {
      element.removeEventListener('scroll', update);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [element, update]);

  const scrollPage = useCallback(
    (direction: -1 | 1) => {
      if (!element) return;
      const first = element.firstElementChild as HTMLElement | null;
      const gap = parseFloat(getComputedStyle(element).columnGap) || 0;
      const step = first ? first.offsetWidth + gap : element.clientWidth;
      const perPage = Math.max(1, Math.floor(element.clientWidth / step));
      const target =
        Math.round(element.scrollLeft / step + direction * perPage) * step;
      element.scrollTo({ left: target, behavior: 'smooth' });
    },
    [element],
  );

  return { ref: setElement, canScrollLeft, canScrollRight, scrollPage };
};
